// docs/17 §5 — one chat everywhere: the World channel (`my_world_messages`, shared by the World
// lobby and the World itself, with the module's system lines) and the Party channel
// (`party_message`, 11 §10.2) once the player is in a party. `variant="panel"` sits in a screen
// column (World lobby, party lobby, World, LocalMap, Aftermath); `variant="dock"` is the collapsible
// dock floated over the encounter board, with an unread count while it is closed. Document screens
// never float it: a fixed corner covers their buttons once the page scrolls.
// 2026-10-23 mockup pass: the log is the mockups' flat party chat — "Name line" with the name in the
// speaker's player colour (`--fk-p1..p24`, chosen or by World seat order), system lines in faint italics, a navy
// Send plate (the screen's one gold primary stays its own). `FlatLog` mirrors the library `ChatLog`
// (same test ids) plus the colour; promote `ChatLine.colour` + a flat variant if others want it.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, EmptyState, Input, Panel, Tabs, type ChatLine } from '../components';
import { useNames, usePartyMessages, usePlayerColourRows, useWorld, useWorldMembers, useWorldMessages } from '../../core/subscriptions/hooks';
import { worldColours } from '../../core/world/colours';
import { useSession } from '../../core/stores/session';
import { identityHex, tsToMs } from '../../core/net/connection';
import { rpc } from '../../core/net/reducers';
import type { Identity, Timestamp } from 'spacetimedb';
import './chat.css';

type Channel = 'world' | 'party';
const MSG_SYSTEM = 1;
const DOCK_KEY = 'fk.chat.dock.open';
const clock = (ms: number | undefined) => new Date(ms ?? 0).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

interface Row { id: bigint; owner: Identity; body: string; at: Timestamp; kind?: number }

type Line = ChatLine & { colour?: number };

/** Player colour index (1-based, `--fk-p<n>`): the player's chosen colour, else the first free one
 *  in World seat order (the keeper first, then by joining; docs/132). */
export function usePlayerColours(worldId: bigint | undefined): Map<string, number> {
  const world = useWorld(worldId);
  const members = useWorldMembers(worldId);
  const chosen = usePlayerColourRows(worldId);
  const keeper = world ? identityHex(world.owner) : '';
  return useMemo(() => new Map([...worldColours(members, chosen, keeper)].map(([o, c]) => [o, c + 1])), [members, chosen, keeper]);
}
export const playerColour = (n: number | undefined) => (n ? `var(--fk-p${n})` : 'var(--fk-gold-500)');

function toLines(rows: Row[], me: string, names: Record<string, string>, colours: Map<string, number>): Line[] {
  return rows.map((m) => {
    const who = identityHex(m.owner);
    return { key: m.id.toString(), who: names[who] ?? who.slice(-6), time: clock(tsToMs(m.at)), body: m.body, me: who === me, system: m.kind === MSG_SYSTEM, colour: colours.get(who) };
  });
}

/** The flat log + line field (the library `ChatLog`'s behaviour and test ids, mockup look). */
function FlatLog({ lines, onSend, placeholder, empty, label, testIds }: { lines: Line[]; onSend: (body: string) => Promise<boolean>; placeholder: string; empty: ReactNode; label: string; testIds: { log: string; input: string; send: string } }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);
  useLayoutEffect(() => { const el = logRef.current; if (el && pinned.current) el.scrollTop = el.scrollHeight; }, [lines.length]);
  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    const ok = await onSend(body);
    setBusy(false);
    if (ok) { setDraft(''); pinned.current = true; }
  };
  return (
    <div className="fk-chatdock__chat" aria-label={label}>
      {lines.length === 0
        ? <div className="fk-chatdock__empty-wrap">{empty ?? <EmptyState glyph="◌" title="Nothing said yet" />}</div>
        : (
          <ol className="fk-chatdock__log" ref={logRef} data-testid={testIds.log} aria-live="polite" aria-label={`${label} log`}
            onScroll={(e) => { const el = e.currentTarget; pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24; }}>
            {lines.map((l) => l.system
              ? <li key={l.key} className="fk-chatdock__line fk-chatdock__line--system" data-testid="chat-line" data-system="true" title={l.time}>{l.body}</li>
              : <li key={l.key} className={`fk-chatdock__line ${l.me ? 'fk-chatdock__line--me' : ''}`} data-testid="chat-line" title={l.time}><b className="fk-chatdock__who" style={{ color: playerColour(l.colour) }}>{l.who}</b> {l.body}</li>)}
          </ol>
        )}
      <div className="fk-chatdock__form">
        <Input data-testid={testIds.input} placeholder={placeholder} value={draft} maxLength={280}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void send(); } e.stopPropagation(); }} aria-label={`${label} message`} />
        <Button disabled={!draft.trim()} loading={busy} onClick={() => void send()} data-testid={testIds.send}>Send</Button>
      </div>
    </div>
  );
}

/** Lines from others newer than the last id the reader saw on that channel. */
function useUnread(rows: Row[], me: string, seen: bigint): number {
  return useMemo(() => rows.filter((m) => m.id > seen && m.kind !== MSG_SYSTEM && identityHex(m.owner) !== me).length, [rows, me, seen]);
}

const lastId = (rows: Row[]): bigint => rows.reduce((a, m) => (m.id > a ? m.id : a), 0n);

export interface ChatDockProps {
  worldId: bigint;
  partyId?: bigint;
  variant?: 'panel' | 'dock';
  /** Which channel opens first (defaults to the party's when there is one). */
  initial?: Channel;
  /** Dock only: `corner` (bottom-left of the window) or `board` (bottom-left of the encounter board, closed by default). */
  placement?: 'corner' | 'board';
  className?: string;
  /** Panel only: a heading in place of the lone World tab (the lobbies' "Party chat"). */
  title?: string;
  /** How many are here (shown as "N here" in the head). */
  here?: number;
}

export function ChatDock({ worldId, partyId, variant = 'panel', initial, placement = 'corner', className = '', title, here }: ChatDockProps) {
  const dockKey = `${DOCK_KEY}.${placement}`;
  const me = useSession((s) => s.identity ?? '');
  const names = useNames(worldId);
  const colours = usePlayerColours(worldId);
  const world = useWorldMessages(worldId) as Row[];
  const party = usePartyMessages(partyId) as Row[];
  const [channel, setChannelState] = useState<Channel>(initial ?? (partyId !== undefined ? 'party' : 'world'));
  // Until the reader picks a tab, a party that appears later (rows arrive after mount) takes over.
  const [picked, setPicked] = useState(initial !== undefined);
  const setChannel = (c: Channel) => { setPicked(true); setChannelState(c); };
  useEffect(() => { if (!picked && partyId !== undefined) setChannelState('party'); }, [picked, partyId]);
  const ch: Channel = channel === 'party' && partyId === undefined ? 'world' : channel;
  const [open, setOpen] = useState(() => { try { const v = localStorage.getItem(dockKey); return v === null ? placement === 'corner' : v === '1'; } catch { return placement === 'corner'; } });
  const visible = variant === 'panel' || open;
  const [seen, setSeen] = useState<{ world: bigint; party: bigint }>({ world: 0n, party: 0n });
  const worldLast = lastId(world), partyLast = lastId(party);
  // Whatever is on screen counts as read.
  useEffect(() => {
    if (!visible) return;
    setSeen((s) => (ch === 'world' ? (s.world === worldLast ? s : { ...s, world: worldLast }) : (s.party === partyLast ? s : { ...s, party: partyLast })));
  }, [visible, ch, worldLast, partyLast]);
  // First load: history is not "unread".
  const [primed, setPrimed] = useState(false);
  useEffect(() => { if (!primed && (world.length || party.length)) { setSeen({ world: worldLast, party: partyLast }); setPrimed(true); } }, [primed, world.length, party.length, worldLast, partyLast]);
  const unreadWorld = useUnread(world, me, seen.world);
  const unreadParty = useUnread(party, me, seen.party);

  const lines = useMemo(() => toLines(ch === 'world' ? world : party, me, names, colours), [ch, world, party, me, names, colours]);
  const send = async (body: string) => {
    const r = ch === 'world' ? await rpc.sendWorldMessage(worldId, body) : partyId !== undefined ? await rpc.sendPartyMessage(partyId, body) : { ok: false };
    return r.ok;
  };
  const count = (n: number) => (n > 0 ? <Badge tone="gold" filled pulse>{n}</Badge> : undefined);
  const tabs = [
    { id: 'world' as const, label: 'World', badge: ch !== 'world' || !visible ? count(unreadWorld) : undefined, testId: 'chat-tab-world' },
    ...(partyId !== undefined ? [{ id: 'party' as const, label: 'Party', badge: ch !== 'party' || !visible ? count(unreadParty) : undefined, testId: 'chat-tab-party' }] : []),
  ];
  const ids = ch === 'world' ? { log: 'world-chat-log', input: 'world-chat-input', send: 'world-chat-send' } : { log: 'chat-log', input: 'chat-input', send: 'chat-send' };
  const hereNote = here !== undefined ? <span className="fk-chatdock__here">{here} here</span> : undefined;
  const head = title && partyId === undefined
    ? <div className="fk-chatdock__head"><h2 className="fk-chatdock__title">{title}</h2>{hereNote}</div>
    : <div className="fk-chatdock__head fk-chatdock__head--tabs"><Tabs label="Chat channel" tabs={tabs} value={ch} onChange={setChannel} className="fk-chatdock__tabs" />{hereNote}</div>;
  const body = (
    <>
      {head}
      <FlatLog key={ch} label={ch === 'world' ? 'World chat' : 'Party chat'} lines={lines} onSend={send} testIds={ids}
        placeholder={ch === 'world' && title !== 'Party chat' ? 'Say something to the whole World…' : 'Say something to the party…'}
        empty={<span className="fk-faint fk-chatdock__empty">{ch === 'world' ? 'Everyone in this World hears this channel — in the lobby and on the road.' : 'Only the party hears this channel.'}</span>} />
    </>
  );

  if (variant === 'panel') {
    return <Panel label="Chat" className={`fk-chatdock fk-chatdock--panel ${className}`} data-testid="chat-dock" data-channel={ch}>{body}</Panel>;
  }
  const toggle = () => { const v = !open; setOpen(v); try { localStorage.setItem(dockKey, v ? '1' : '0'); } catch { /* ignore */ } };
  const unread = unreadWorld + unreadParty;
  return (
    <div className={`fk-chatdock fk-chatdock--dock fk-chatdock--${placement} fk-hit ${open ? 'fk-chatdock--open' : ''} ${className}`} data-testid="chat-dock" data-channel={ch} data-open={open}>
      <button type="button" className="fk-chatdock__toggle" onClick={toggle} aria-expanded={open} data-testid="chat-dock-toggle">
        <span aria-hidden="true">✉</span> Chat {!open && count(unread)}<span className="fk-chatdock__chev" aria-hidden="true">{open ? '▾' : '▴'}</span>
      </button>
      {open && <Panel variant="solid" label="Chat" className="fk-chatdock__panel">{body}</Panel>}
    </div>
  );
}
